import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Typography, Checkbox, FormControlLabel } from '@mui/material';
import Button from 'components/Button';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { validationRegexes } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { Schema } from 'further-types/organisation';
import api from 'lib/trpcClient';
import TextField from 'components/FormElements/AppTextInput';
import PageContainer from 'components/PageContainer';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import { SortOrder } from 'further-types/utils';

import {
  createOrganisationViewRoute,
  ORGANISATION,
} from 'adminConstants/routes';
import FieldRow from 'components/FormElements/FieldRow';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { FirmSelector } from 'components/FirmSelector';
import { useOrganisationAllowedDomains } from 'hooks/data/organisation/useOrganisationAllowedDomains';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import ButtonGroup from 'components/ButtonGroup';
import AssociatedFirms from 'components/FormElements/AssociatedFirms';
import AddOrganisationConfirmationDialog from './AddOrganisationConfirmationDialog';

type OrganisationData = {
  associatedFirms: Array<string>;
  firmId: string;
  adviserDropdown: number;
  type: Schema.OrganisationHeirarchyType;
  parentOrganisation: {
    parentId: string | null;
    name: string | null;
  };
  name: string;
  fcaNumber: string;
  canBeEditedByFirm?: boolean;
} & (
  | {
      skipAccountOwner: true;
      email: null;
      firstName: null;
      lastName: null;
    }
  | {
      skipAccountOwner: false;
      email: string;
      firstName: string;
      lastName: string;
    }
);

const useStyles = makeStyles()((theme) => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  accOwnerTitle: {
    color: theme.palette.text.darkBodyText,
    margin: '1rem 0 0.5rem 0',
    fontSize: 20,
  },
  accOwnerSubtitle: {
    color: theme.palette.text.darkBodyText,
    fontSize: 14,
  },
}));

const AddUpdateOrganisation = () => {
  const history = useHistory();
  const { classes } = useStyles();
  const { id } = useParams<{ id?: string }>();
  const isBeingEdited = !!id;
  const { firmId, isSuperAdmin } = useGetRole();
  const [editPermission] = useHasPermissions([
    RolePermission.EditOrganisations,
  ]);
  const addOrganisationConfirmationDisclosure = useDisclosure();

  const parentOrganisations = api.organisation.list.useQuery({
    isParent: true,
    firmId,
    order: SortOrder.Desc,
    orderBy: 'createdAt',
  });
  const { mutate: createOrganisation, isPending: isCreatingOrganisation } =
    api.organisation.create.useMutation(
      useHandleTrpcMutation<{ id: string }>()({
        successMessage: 'Organisation created successfully.',
        onSuccess: (data) => {
          addOrganisationConfirmationDisclosure.onClose();
          history.push(createOrganisationViewRoute(data.id));
        },
        onError: addOrganisationConfirmationDisclosure.onClose,
      }),
    );

  const { mutate: updateOrganisation, isPending: isUpdatingOrganisation } =
    api.organisation.updateById.useMutation(
      useHandleTrpcMutation<{ id: string }>()({
        successMessage: 'Organisation updated successfully.',
        onSuccess: (data) => {
          addOrganisationConfirmationDisclosure.onClose();
          history.push(createOrganisationViewRoute(data.id));
        },
      }),
    );

  const { data: organisation } = api.organisation.getById.useQuery(
    { id: id ?? '' },
    { enabled: !!id },
  );

  const [organisationData, setOrganisationData] = useState<OrganisationData>({
    associatedFirms: [],
    firmId: firmId || '',
    adviserDropdown: 0,
    type: Schema.OrganisationHeirarchyType.Parent,
    parentOrganisation: {
      parentId: null,
      name: null,
    },
    name: '',
    fcaNumber: '',
    skipAccountOwner: false,
    email: '',
    firstName: '',
    lastName: '',
    canBeEditedByFirm: false,
  });
  const debouncedOrganisationName = useDebounce(organisationData?.name, 300);
  const organisationAllowedDomains = useOrganisationAllowedDomains({
    name: debouncedOrganisationName,
  });
  const allowedDomains = organisationAllowedDomains.data?.allowedDomains || [];

  const inputDisabled =
    isBeingEdited &&
    (!editPermission || (!isSuperAdmin && !organisationData.canBeEditedByFirm));

  const [errorText, setErrorText] = useState<Record<string, string>>({});
  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Organisation', link: '/organisation' },
    {
      label: id ? 'Update Organisation' : 'Add Organisation',
      link: '/',
      isActive: true,
    },
  ];
  const record = [
    { key: 1, name: 'Adviser organisation' },
    { key: 2, name: 'Adviser parent organisation' },
  ];

  const validate = () => {
    const errors: Record<string, string> = {};
    let isError = false;
    if (!id && !organisationData?.firmId) {
      errors.firmId = 'Please select firm.';
      isError = true;
    }
    if (!organisationData?.adviserDropdown) {
      errors.adviserDropdown = 'Please select organisation type.';
      isError = true;
    }
    if (!organisationData?.name) {
      errors.name = 'Please enter organisation name.';
      isError = true;
    }
    if (!organisationData?.fcaNumber) {
      errors.fcaNumber = 'Please enter FCA registration number.';
      isError = true;
    } else if (
      !new RegExp(/^[a-zA-Z0-9]+$/i).test(organisationData?.fcaNumber)
    ) {
      errors.fcaNumber = 'An FCA number can only contain letters or numbers.';
      isError = true;
    }
    if (!id) {
      const { skipAccountOwner, email, firstName, lastName } = organisationData;
      if (skipAccountOwner) {
        // Allow creation, skip user validation
      } else {
        if (!email) {
          errors.email = 'Please enter user email.';
          isError = true;
        } else if (!validationRegexes.email.test(email)) {
          errors.email = 'Please enter a valid email address.';
          isError = true;
        }
        if (!firstName) {
          errors.firstName = 'Please enter account owner first name.';
          isError = true;
        }
        if (!lastName) {
          errors.lastName = 'Please enter account owner surname.';
          isError = true;
        }
      }
    }
    setErrorText(errors);

    return {
      errors,
      isError,
    };
  };

  const onSubmit = async (shouldAskForConfirmation: boolean) => {
    const validateData = validate();
    if (!validateData.isError) {
      const { parentOrganisation, ...rest } = organisationData;
      const value = {
        ...rest,
        parentId:
          organisationData?.type === Schema.OrganisationHeirarchyType.Child
            ? parentOrganisation?.parentId
            : null,
      };
      if (!id) {
        if (
          !isSuperAdmin &&
          shouldAskForConfirmation &&
          allowedDomains.length > 0 &&
          value.email &&
          !allowedDomains.includes(value.email.split('@')[1])
        ) {
          return addOrganisationConfirmationDisclosure.onOpen();
        }

        createOrganisation(value);
      } else {
        updateOrganisation({ ...value, id });
      }
    }
  };

  useEffect(() => {
    if (organisation && parentOrganisations.isFetched) {
      const {
        name,
        parentId,
        fcaNumber,
        type,
        canBeEditedByFirm,
        associatedFirms,
      } = organisation;
      const value: OrganisationData = {
        ...organisationData,
        name,
        fcaNumber,
        adviserDropdown:
          type === Schema.OrganisationHeirarchyType.Child ? 1 : 2,
        type,
        canBeEditedByFirm,
        associatedFirms,
      };
      if (type === Schema.OrganisationHeirarchyType.Child) {
        const parentOrganisation = parentOrganisations.data?.organisation?.find(
          (org) => parentId?._id === org?._id,
        );
        value.parentOrganisation = {
          name: parentOrganisation?.name ?? null,
          parentId: parentOrganisation?._id ?? null,
        };
      }
      setOrganisationData(value);
    }
  }, [parentOrganisations.isFetched, organisation]);

  return (
    <React.Fragment>
      <PageContainer
        heading={
          isBeingEdited
            ? 'Organisation: Update organisation'
            : 'Organisation: Create organisation'
        }
        breadcrumbs={breadcrumbs}
      >
        <PageContent>
          <RecordView>
            {!isBeingEdited && isSuperAdmin && (
              <FieldRow title="Firm*" centerTitle>
                <FirmSelector
                  firmId={organisationData?.firmId}
                  onChange={(firmId) =>
                    setOrganisationData({
                      ...organisationData,
                      firmId,
                    })
                  }
                  error={!!errorText?.firmId}
                  helperText={errorText?.firmId}
                  disabled={!!inputDisabled}
                  required
                />
              </FieldRow>
            )}
            {isBeingEdited && isSuperAdmin && (
              <AssociatedFirms
                title="Associated firms"
                associatedFirmIds={organisationData?.associatedFirms ?? []}
                onChange={(firmIds) =>
                  setOrganisationData({
                    ...organisationData,
                    associatedFirms: firmIds,
                  })
                }
              />
            )}

            <FieldRow title="Organisation type*" centerTitle>
              <AppSelectBox
                required
                data={record}
                valueKey="key"
                name="adviserDropdown"
                placeholder="Select organisation type"
                value={organisationData?.adviserDropdown || ''}
                labelKey="name"
                variant="outlined"
                error={!!errorText?.adviserDropdown}
                helperText={errorText?.adviserDropdown}
                onChange={(event) => {
                  if (event.target.value === 1) {
                    parentOrganisations.refetch();
                  }
                  setOrganisationData({
                    ...organisationData,
                    adviserDropdown: event.target.value as number,
                    type:
                      event.target.value === 1
                        ? Schema.OrganisationHeirarchyType.Child
                        : Schema.OrganisationHeirarchyType.Parent,
                  });
                }}
                disabled={!!inputDisabled}
              />
            </FieldRow>
            {(typeof organisationData?.adviserDropdown === 'string' ||
              organisationData?.adviserDropdown === 1) && (
              <FieldRow
                title="Select parent organisation"
                informationText="(if applicable)"
                centerTitle
              >
                <Autocomplete
                  id="parentId"
                  options={parentOrganisations.data?.organisation ?? []}
                  getOptionLabel={(option) => option.name ?? ''}
                  getOptionKey={(option) => option._id}
                  filterSelectedOptions
                  value={
                    parentOrganisations.data?.organisation.find(
                      (org) =>
                        org._id ===
                        organisationData?.parentOrganisation?.parentId,
                    ) ?? null
                  }
                  onChange={(_, newValue) => {
                    setOrganisationData({
                      ...organisationData,
                      parentOrganisation: {
                        name: newValue?.name ?? null,
                        parentId: newValue?._id ?? null,
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select parent organisation"
                    />
                  )}
                  disabled={!!inputDisabled}
                />
              </FieldRow>
            )}
            <FieldRow
              title={
                typeof organisationData?.adviserDropdown === 'string' ||
                organisationData?.adviserDropdown === 1
                  ? 'Organisation name*'
                  : 'Parent organisation name*'
              }
              centerTitle
            >
              <TextField
                required
                name="name"
                fullWidth
                placeholder={
                  typeof organisationData?.adviserDropdown === 'string' ||
                  organisationData?.adviserDropdown === 1
                    ? 'Enter organisation name'
                    : 'Enter parent organisation name'
                }
                value={organisationData?.name}
                error={!!errorText?.name}
                helperText={errorText?.name}
                onChange={(event) =>
                  setOrganisationData({
                    ...organisationData,
                    name: event.target.value,
                  })
                }
                disabled={!!inputDisabled}
              />
            </FieldRow>
            <FieldRow title="FCA firm reference number*" centerTitle>
              <TextField
                required
                name="fcaNumber"
                placeholder="Enter FCA firm reference number"
                fullWidth
                value={organisationData?.fcaNumber}
                error={!!errorText?.fcaNumber}
                helperText={errorText?.fcaNumber}
                onChange={(event) =>
                  setOrganisationData({
                    ...organisationData,
                    fcaNumber: event.target.value.toUpperCase(),
                  })
                }
                disabled={!!inputDisabled}
              />
            </FieldRow>
            {!isBeingEdited && (
              <>
                <Typography variant="h3" className={classes.accOwnerTitle}>
                  Account owner details
                </Typography>
                <p className={classes.accOwnerSubtitle}>
                  Additional users can be added on the organisation page after
                  the organisation has been created
                </p>

                <FieldRow centerTitle>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={organisationData?.skipAccountOwner}
                        onChange={(event) =>
                          setOrganisationData({
                            ...organisationData,
                            ...(event.target.checked
                              ? {
                                  skipAccountOwner: true,
                                  email: null,
                                  firstName: null,
                                  lastName: null,
                                }
                              : {
                                  skipAccountOwner: false,
                                  email: '',
                                  firstName: '',
                                  lastName: '',
                                }),
                          })
                        }
                        disabled={!!inputDisabled}
                      />
                    }
                    label="Create organisation without account owner"
                  />
                </FieldRow>

                {!organisationData?.skipAccountOwner && (
                  <>
                    <FieldRow title="Account owner email address*" centerTitle>
                      <TextField
                        required
                        name="email"
                        placeholder="Enter email"
                        fullWidth
                        value={organisationData?.email}
                        error={!!errorText?.email}
                        helperText={errorText?.email}
                        onChange={(event) =>
                          setOrganisationData({
                            ...organisationData,
                            email: event.target.value,
                          })
                        }
                        disabled={!!inputDisabled}
                      />
                    </FieldRow>
                    <FieldRow title="Account owner first name*" centerTitle>
                      <TextField
                        required
                        name="firstName"
                        placeholder="Enter first name"
                        fullWidth
                        value={organisationData?.firstName}
                        error={!!errorText?.firstName}
                        helperText={errorText?.firstName}
                        onChange={(event) =>
                          setOrganisationData({
                            ...organisationData,
                            firstName: event.target.value,
                          })
                        }
                        disabled={!!inputDisabled}
                      />
                    </FieldRow>
                    <FieldRow title="Account owner surname*" centerTitle>
                      <TextField
                        required
                        name="lastName"
                        placeholder="Enter surname"
                        fullWidth
                        value={organisationData?.lastName}
                        error={!!errorText?.lastName}
                        helperText={errorText?.lastName}
                        onChange={(event) =>
                          setOrganisationData({
                            ...organisationData,
                            lastName: event.target.value,
                          })
                        }
                        disabled={!!inputDisabled}
                      />
                    </FieldRow>
                  </>
                )}
              </>
            )}
            <ButtonGroup>
              <Link to={ORGANISATION}>
                <Button color="primary" variant="outlined">
                  Cancel
                </Button>
              </Link>
              {!inputDisabled && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.marginLeftBtn}
                  onClick={() => onSubmit(true)}
                  loading={isCreatingOrganisation || isUpdatingOrganisation}
                >
                  {id ? 'Update organisation' : 'Add organisation'}
                </Button>
              )}
            </ButtonGroup>
            <AddOrganisationConfirmationDialog
              disclosure={addOrganisationConfirmationDisclosure}
              onConfirmAddOrganisation={() => onSubmit(false)}
              allowedDomains={allowedDomains}
              submitting={isCreatingOrganisation}
            />
          </RecordView>
        </PageContent>
      </PageContainer>
    </React.Fragment>
  );
};

export default AddUpdateOrganisation;
